<template>
  <div class="amc-level-select-container">
    <!-- 顶部导航栏 -->
    <div class="amc-header">
      <div class="header-left">
        <i class="el-icon-close close-btn" @click="goBack"></i>
        <h1 class="page-title">选择级别</h1>
      </div>
    </div>
    <!-- el-empty v-if="!isLoading" description="请先登录" /> -->
    <!-- 主要内容区域 -->
    <div class="content-area">
      <!-- 加载状态 -->
      <div v-if="isLoading" class="loading-section">
        <div class="loading-spinner">
          <i class="el-icon-loading"></i>
        </div>
        <p class="loading-text">正在检查您的学习计划...</p>
      </div>

      <!-- 选择界面 -->
      <div v-else>
        <!-- 标题区域 -->
        <div class="title-section">
          <h2 class="main-title">选择您的{{ courseTypeTitle }}级别</h2>
          <p class="subtitle">AI将为您制定个性化学习方案</p>
        </div>

      <!-- 级别选择 -->
      <div class="level-selection">
        <div
          v-for="level in displayedLevels"
          :key="level.id"
          class="level-card"
          :class="{ 'selected': selectedLevel === level.id }"
          @click="selectLevel(level.id)"
        >
          <div class="level-header">
            <h3 class="level-title">{{ level.title }}</h3>
            <div v-if="level.grade" class="grade-tag">{{ level.grade }}</div>
          </div>
          <p class="level-description">{{ level.description }}</p>
        </div>
      </div>

      <!-- 知识背景选择 -->
      <div class="knowledge-background-selection">
        <h3 class="section-title">您的知识背景</h3>
        <div class="knowledge-options">
          <div
            v-for="option in knowledgeBackgroundOptions"
            :key="option.value"
            class="knowledge-option"
            :class="{ 'selected': selectedKnowledgeBackground === option.value }"
            @click="selectKnowledgeBackground(option.value)"
          >
            <div class="option-icon">
              <i :class="option.icon"></i>
            </div>
            <div class="option-content">
              <h4 class="option-title">{{ option.title }}</h4>
              <p class="option-description">{{ option.description }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- 参赛年份选择 -->
      <div class="year-selection">
        <h3 class="section-title">选择参赛年份</h3>
        <el-select
          v-model="selectedYear"
          placeholder="请选择年份"
          class="year-selector"
        >
          <el-option
            v-for="year in availableYears"
            :key="year"
            :label="year + '年'"
            :value="year">
          </el-option>
        </el-select>
      </div>

      <div v-if="showSessionSelector" class="year-selection">
        <h3 class="section-title">选择考试场次</h3>
        <el-select
          v-model="selectedSessionCode"
          placeholder="请选择考试场次"
          class="year-selector"
          clearable
        >
          <el-option
            v-for="item in sessionOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value">
          </el-option>
        </el-select>
      </div>

      <!-- 开始按钮 -->
      <div class="action-section">
        <el-button
          type="primary"
          class="start-button"
          :disabled="!selectedLevel || !selectedKnowledgeBackground || !selectedYear || (showSessionSelector && !selectedSessionCode)"
          @click="startPreparation"
        >
          下一页
        </el-button>
      </div>
      </div>
    </div>
  </div>
</template>

<script>
// 通用接口
import { createPlan, getPlansByUserId, getPlanByUserIdAndRepoId, updatePlan } from '@/api/studyPlan'
import { getTokenInfo } from '@/utils/jwtUtils'
import { updateUserStatus } from '@/utils/userStatus'
import { getCourseCategories, getCoursesByCategoryId } from '@/api/course'
import { fetchPaging as fetchRepoList } from '@/api/repo'
import { listExamScheduleConfigs } from '@/api/examScheduleConfig'

export default {
  name: 'AMCLevelSelect',
  data() {
    return {
      selectedLevel: null,
      selectedLevelTitle: null,
      selectedKnowledgeBackground: null,
      selectedYear: new Date().getFullYear() + 1, // 默认下一年
      selectedSessionCode: null,
      isLoading: true, // 添加加载状态
      forcedLevels: null,
      courseTypeId: null,
      courseTypeCode: null,
      courseTypeTitle: '课程',
      dynamicLevels: [],
      knowledgeBackgroundOptions: [
        {
          value: 'never',
          title: '从来没有',
          description: '从未接触过AMC相关知识和竞赛内容',
          icon: 'el-icon-question'
        },
        {
          value: 'basic',
          title: '有一定了解',
          description: '对AMC竞赛有一定了解，学习过部分相关内容',
          icon: 'el-icon-info'
        },
        {
          value: 'proficient',
          title: '熟练掌握',
          description: '对AMC竞赛内容非常熟悉，有丰富的学习和参赛经验',
          icon: 'el-icon-star-on'
        }
      ],
      amcLevels: [
        {
          id: 'amc8',
          title: 'AMC 8',
          grade: '8年级及以下 (初二及以下)',
          description: '适合初二及以下学生参加，题目相对基础，注重数学思维培养和基础知识运用。涵盖算术、代数、几何、数论等内容。考试时间40分钟，25道选择题。'
        },
        {
          id: 'amc10',
          title: 'AMC 10',
          grade: '10年级及以下 (高一及以下)',
          description: '适合高一及以下学生参加，难度适中，涵盖代数、几何、数论、概率等内容。题目更注重逻辑推理和问题解决能力。考试时间75分钟，25道选择题。'
        },
        {
          id: 'amc12',
          title: 'AMC 12',
          grade: '12年级及以下 (高三及以下)',
          description: '适合高三及以下学生参加，难度较高，包含高等代数、三角函数、概率统计、解析几何等内容。题目具有挑战性，需要较强的数学基础和创新思维。考试时间75分钟，25道选择题。'
        }
      ],
      availableYears: [],
      sessionOptions: []
    }
  },
  computed: {
    isAmcMode() {
      const code = String(this.courseTypeCode || '').toLowerCase()
      if (code && code.includes('amc')) return true
      const q = String(this.$route.query.level || '').toLowerCase()
      if (q === 'amc8' || q === 'amc10' || q === 'amc12') return true
      // 如果路径包含 'amc' 且没有明确的课程类型参数，默认认为是 AMC 模式
      const path = String(this.$route.path || '').toLowerCase()
      if (path.includes('amc') && !code) return true
      return false
    },
    displayedLevels() {
      if (!this.isAmcMode) return Array.isArray(this.dynamicLevels) ? this.dynamicLevels : []
      if (!Array.isArray(this.forcedLevels) || !this.forcedLevels.length) return this.amcLevels
      return this.amcLevels.filter(l => this.forcedLevels.includes(l.id))
    },
    showSessionSelector() {
      return !this.isAmcMode && Array.isArray(this.sessionOptions) && this.sessionOptions.length > 0
    }
  },
  watch: {
    selectedLevel() {
      this.syncSelectedLevelMeta()
      this.loadSessionOptions()
    },
    selectedYear() {
      this.loadSessionOptions()
    }
  },
  async created() {
    this.hideSidebarAndNavbar()
    this.generateAvailableYears()

    this.courseTypeId = this.$route.query.courseTypeId || null
    this.courseTypeCode = this.$route.query.courseTypeCode || null
    this.courseTypeTitle = String(this.courseTypeCode || '课程').toUpperCase()

    if (!this.isAmcMode) {
      await this.loadDynamicLevels()
      const ql = String(this.$route.query.level || '').trim()
      if (ql) this.selectedLevel = ql
      this.syncSelectedLevelMeta()
    }
    if (this.$route.query.sessionCode) {
      this.selectedSessionCode = String(this.$route.query.sessionCode)
    }
    await this.loadSessionOptions()

    // 若从入口指定level，则限制可选范围
    const queryLevel = this.$route.query.level
    if (this.isAmcMode && (queryLevel === 'amc10' || queryLevel === 'amc12')) {
      this.forcedLevels = [queryLevel]
      this.selectedLevel = queryLevel
    }

    // 如果是重新制定计划模式（recreate=true），跳过自动跳转检查
    const isRecreate = this.$route.query.recreate === 'true'
    if (isRecreate) {
      // 重新制定计划模式，直接显示选择界面，不检查已有计划
      this.isLoading = false
      return
    }

    // 检查用户是否已有学习计划
    const hasPlan = await this.checkUserAmcInfo()
    if (hasPlan) return

    // 检查完成，显示选择界面
    this.isLoading = false
  },
  beforeDestroy() {
    this.showSidebarAndNavbar()
  },
  methods: {
    async loadDynamicLevels() {
      try {
        const cid = this.courseTypeId != null ? String(this.courseTypeId) : ''
        if (!cid) {
          this.dynamicLevels = []
          return
        }
        const res = await getCourseCategories()
        const list = (res && res.code === 1 && Array.isArray(res.data)) ? res.data : []
        const ct = list.find(x => String(x?.id) === cid)
        this.courseTypeTitle = String(this.courseTypeCode || ct?.code || ct?.name || '课程').toUpperCase()
        const courses = await getCoursesByCategoryId(cid)
        const arr = Array.isArray(courses) ? courses : []
        this.dynamicLevels = arr
          .map(c => {
            const title = String(c?.courseName || c?.course_name || c?.name || '').trim()
            const code = String(c?.courseCode || c?.course_code || c?.code || title).trim()
            if (!title && !code) return null
            return {
              id: code || title,
              title: title || code,
              grade: '',
              description: ''
            }
          })
          .filter(Boolean)
      } catch (e) {
        this.dynamicLevels = []
      }
    },

    async loadSessionOptions() {
      if (this.isAmcMode || !this.selectedLevel || !this.selectedYear) {
        this.sessionOptions = []
        this.selectedSessionCode = null
        return
      }
      try {
        const courseTypeCode = String(this.courseTypeCode || '').toUpperCase()
        const subjectCode = this.getSelectedSubjectCode()
        const res = await listExamScheduleConfigs({
          courseTypeCode,
          subjectCode,
          targetYear: this.selectedYear,
          status: 1
        })
        const list = (res && (res.data !== undefined ? res.data : res)) || []
        const rows = Array.isArray(list) ? list : []
        this.sessionOptions = rows
          .filter(item => item && item.sessionCode)
          .map(item => ({
            value: item.sessionCode,
            label: `${item.sessionCode} (${this.formatExamDate(item.examDate)})`
          }))
        if (this.sessionOptions.length === 1 && !this.selectedSessionCode) {
          this.selectedSessionCode = this.sessionOptions[0].value
        }
        if (this.selectedSessionCode && !this.sessionOptions.some(item => item.value === this.selectedSessionCode)) {
          this.selectedSessionCode = null
        }
      } catch (e) {
        this.sessionOptions = []
        this.selectedSessionCode = null
      }
    },

    normalizeSubjectCode(value) {
      const raw = String(value || '').trim()
      if (!raw) return ''
      const upper = raw.toUpperCase().replace(/\s+/g, '')
      if (upper === 'AMC8' || upper === 'AMC10' || upper === 'AMC12') return upper
      if (upper.startsWith('GESP')) {
        const levelMatch = upper.match(/LEVEL(\d+)/) || upper.match(/^GESP(\d+)$/)
        if (levelMatch) return `GESPLEVEL${levelMatch[1]}`
      }
      if (upper.startsWith('CCF-GESP')) {
        const levelMatch = upper.match(/LEVEL(\d+)/)
        if (levelMatch) return `GESPLEVEL${levelMatch[1]}`
      }
      return upper
    },

    syncSelectedLevelMeta() {
      const picked = (this.displayedLevels || []).find(level => level.id === this.selectedLevel)
      if (picked) {
        this.selectedLevelTitle = picked.title || picked.id
        return
      }
      this.selectedLevelTitle = this.$route.query.levelTitle || this.selectedLevel
    },

    formatExamDate(value) {
      if (!value) return '--'
      const date = new Date(String(value).replace(' ', 'T'))
      if (isNaN(date.getTime())) return value
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${date.getFullYear()}-${month}-${day}`
    },

    async resolveRepoIdFromRepoTable() {
      // 优先使用路由中传入的 repoId
      const routeRepoId = Number(this.$route?.query?.repoId)
      if (routeRepoId && !Number.isNaN(routeRepoId)) {
        return routeRepoId
      }

      const titleRaw = String(this.$route.query.level || this.selectedLevel || '').trim()
      const titleKey = titleRaw.toLowerCase()
      const courseId = this.courseTypeId != null ? Number(this.courseTypeId) : NaN
      if (!titleKey) return null

      const res = await fetchRepoList({ pageNum: 1, pageSize: 1000 }).catch(() => null)
      const list = Array.isArray(res?.data)
        ? res.data
        : (Array.isArray(res?.data?.data) ? res.data.data : (Array.isArray(res) ? res : []))
      const rows = Array.isArray(list) ? list : []

      const isDel = (r) => {
        const del = Number(r?.isDeleted != null ? r.isDeleted : r?.is_deleted)
        return del === 1
      }
      const matchTitle = (t) => {
        const s = String(t || '').trim().toLowerCase()
        if (!s) return false
        if (s === titleKey) return true
        return s.endsWith(titleKey) || s.includes(titleKey) || titleKey.includes(s)
      }
      const matchCourseId = (r) => {
        if (!courseId || Number.isNaN(courseId)) return true
        const cid = Number(r?.courseId != null ? r.courseId : r?.course_id)
        return cid === courseId
      }

      let hit = rows.find(r => {
        if (!r) return false
        if (isDel(r)) return false
        if (!matchCourseId(r)) return false
        return matchTitle(r.title)
      })
      if (!hit) {
        hit = rows.find(r => {
          if (!r) return false
          if (isDel(r)) return false
          return matchTitle(r.title)
        })
      }

      const id = hit && hit.id != null ? Number(hit.id) : null
      return id && !Number.isNaN(id) ? id : null
    },
    // 隐藏侧边栏和导航栏
    hideSidebarAndNavbar() {
      this.$store.dispatch('app/closeSideBar', { withoutAnimation: false })

      const sidebar = document.querySelector('.sidebar-container')
      const navbar = document.querySelector('.navbar')

      if (sidebar) {
        sidebar.style.display = 'none'
      }
      if (navbar) {
        navbar.style.display = 'none'
      }

      const appMain = document.querySelector('.app-main')
      if (appMain) {
        appMain.style.padding = '0'
        appMain.style.margin = '0'
      }
    },

    // 恢复侧边栏和导航栏
    showSidebarAndNavbar() {
      this.$store.dispatch('app/openSideBar', { withoutAnimation: false })

      const sidebar = document.querySelector('.sidebar-container')
      const navbar = document.querySelector('.navbar')

      if (sidebar) {
        sidebar.style.display = ''
      }
      if (navbar) {
        navbar.style.display = ''
      }

      const appMain = document.querySelector('.app-main')
      if (appMain) {
        appMain.style.padding = ''
        appMain.style.margin = ''
      }
    },

    // 生成可选年份（当前年份到未来5年）
    generateAvailableYears() {
      const currentYear = new Date().getFullYear()
      for (let i = 0; i < 6; i++) {
        this.availableYears.push(currentYear + i)
      }
    },

    // 选择AMC级别
    selectLevel(levelId) {
      this.selectedLevel = levelId
      this.syncSelectedLevelMeta()
    },

    getSelectedSubjectCode() {
      return this.normalizeSubjectCode(this.selectedLevel)
    },

    // 选择知识背景
    selectKnowledgeBackground(backgroundValue) {
      this.selectedKnowledgeBackground = backgroundValue
    },

    // 获取AMC级别对应的数字
    getAmcLevelNumber(level) {
      const levelMap = {
        'amc8': 14,
        'amc10': 15,
        'amc12': 16
      }
      return levelMap[level] || 14
    },

    // 获取知识背景对应的数字
    getKnowledgeBackgroundNumber(background) {
      const backgroundMap = {
        'never': 1, // 从来没有
        'basic': 2, // 有一定了解
        'proficient': 3 // 熟练掌握
      }
      return backgroundMap[background] || 1
    },

    // 检查用户是否已有学习计划（使用通用接口）
    async checkUserAmcInfo() {
      try {
        const userInfo = getTokenInfo()
        if (!userInfo || !userInfo.id) {
          return false
        }

        const userId = userInfo.id
        const queryLevel = this.$route.query.level
        const levelToRepo = { 'amc8': 14, 'amc10': 15, 'amc12': 16 }

        // 非 AMC（如袋鼠）：按 repoId 精确匹配该课程的计划，避免被已有 AMC 计划误重定向
        if (!this.isAmcMode) {
          const rid = await this.resolveRepoIdFromRepoTable()
          if (!rid) {
            if (queryLevel) this.selectedLevel = String(queryLevel).trim()
            return false
          }

          const planRes = await getPlansByUserId(userId)
          const plans = planRes?.data || []
          const planList = (Array.isArray(plans) ? plans : []).filter(p => Number(p?.repoId) === Number(rid))
          const matchedPlan = planList.find(p => Number(p?.isActive) === 1) || planList[0]
          if (matchedPlan) {
            this.$router.replace({
              path: '/amc-course',
              query: {
                level: String(queryLevel || this.selectedLevel || ''),
                knowledgeBackground: this.getKnowledgeBackgroundKey(Number(matchedPlan.knowledgeLevel)),
                year: Number(matchedPlan.targetYear),
                courseTypeId: this.courseTypeId,
                courseTypeCode: this.courseTypeCode,
                sessionCode: matchedPlan.sessionCode || undefined
              }
            })
            return true
          }

          if (queryLevel) this.selectedLevel = String(queryLevel).trim()
          return false
        }

        // 使用通用接口查询学习计划
        const planRes = await getPlansByUserId(userId)
        const plans = planRes?.data || []

        // 从 t_repo 表动态获取 AMC 的 repoId
        const repoRes = await fetchRepoList({ pageNum: 1, pageSize: 1000 }).catch(() => null)
        const repoList = Array.isArray(repoRes?.data)
          ? repoRes.data
          : (Array.isArray(repoRes?.data?.data) ? repoRes.data.data : [])

        // 找出所有 AMC 相关的 repo（title 包含 AMC8/AMC10/AMC12）
        const amcRepoIds = repoList
          .filter(r => {
            if (Number(r?.isDeleted || r?.is_deleted) === 1) return false
            const title = String(r?.title || '').toUpperCase()
            return title === 'AMC8' || title === 'AMC10' || title === 'AMC12'
          })
          .map(r => Number(r?.id))

        // 根据 repoId 判断是否为 AMC 计划
        const amcPlans = (Array.isArray(plans) ? plans : []).filter(p => {
          const rid = Number(p?.repoId)
          return amcRepoIds.includes(rid)
        })

        const pickPlan = (arr) => {
          if (!Array.isArray(arr) || !arr.length) return null
          return arr.find(p => Number(p?.isActive) === 1) || arr[0]
        }

        if (queryLevel && levelToRepo[queryLevel]) {
          const targetRepo = levelToRepo[queryLevel]
          const matchedPlan = pickPlan(amcPlans.filter(p => Number(p?.repoId) === Number(targetRepo)))
          if (matchedPlan) {
            this.$router.replace({
              path: '/amc-course',
              query: {
                level: queryLevel,
                knowledgeBackground: this.getKnowledgeBackgroundKey(Number(matchedPlan.knowledgeLevel)),
                year: Number(matchedPlan.targetYear),
                sessionCode: matchedPlan.sessionCode || undefined
              }
            })
            return true
          }
          // 有指定等级但没找到计划，预选该等级
          this.selectedLevel = queryLevel
          return false
        }

        const anyPlan = pickPlan(amcPlans)
        if (anyPlan) {
          this.$router.replace({
            path: '/amc-course',
            query: {
              level: this.getAmcLevelKey(Number(anyPlan.repoId)),
              knowledgeBackground: this.getKnowledgeBackgroundKey(Number(anyPlan.knowledgeLevel)),
              year: Number(anyPlan.targetYear),
              sessionCode: anyPlan.sessionCode || undefined
            }
          })
          return true
        }

        // 如果有指定等级但没有计划，预选该等级
        if (queryLevel && levelToRepo[queryLevel]) {
          this.selectedLevel = queryLevel
        }

        return false
      } catch (error) {
        console.error('检查用户学习计划失败:', error)
        return false
      }
    },

    // 根据数字获取AMC级别key
    getAmcLevelKey(levelNumber) {
      const levelMap = {
        14: 'amc8',
        15: 'amc10',
        16: 'amc12'
      }
      return levelMap[levelNumber] || 'amc8'
    },

    // 根据数字获取知识背景key
    getKnowledgeBackgroundKey(backgroundNumber) {
      const backgroundMap = {
        1: 'never', // 从来没有
        2: 'basic', // 有一定了解
        3: 'proficient' // 熟练掌握
      }
      return backgroundMap[backgroundNumber] || 'never'
    },

    // 返回上一页
    goBack() {
      this.$router.go(-1)
    },

    // 开始备考
    async startPreparation() {
      if (!this.selectedLevel || !this.selectedKnowledgeBackground || !this.selectedYear || (this.showSessionSelector && !this.selectedSessionCode)) {
        this.$message.warning(this.showSessionSelector ? '请选择级别、知识背景、参赛年份和考试场次' : '请选择级别、知识背景和参赛年份')
        return
      }

      try {
        // 跳转到计划详情页面，携带已选参数
        const planQuery = {
          level: this.selectedLevel,
          levelTitle: this.selectedLevelTitle || this.selectedLevel,
          subjectCode: this.getSelectedSubjectCode() || undefined,
          knowledgeBackground: this.selectedKnowledgeBackground,
          year: this.selectedYear,
          courseTypeId: this.courseTypeId,
          courseTypeCode: this.courseTypeCode
        }
        if (this.selectedSessionCode) planQuery.sessionCode = this.selectedSessionCode
        // 透传 repoId（从上游页面传入）
        const repoId = this.$route?.query?.repoId
        if (repoId) planQuery.repoId = repoId
        this.$router.replace({
          path: '/amc-plan-details',
          query: planQuery
        })
      } catch (error) {
        console.error('跳转到计划详情失败:', error)
        this.$message.error('跳转失败，请重试')
      }
    },

    // 保存用户选择到后端（使用通用接口）
    async saveUserSelection() {
      try {
        const userInfo = getTokenInfo()
        if (!userInfo || !userInfo.id) {
          throw new Error('用户ID不存在，请重新登录')
        }

        const userId = userInfo.id
        const username = userInfo.username || userInfo.userName || userInfo.name || (JSON.parse(localStorage.getItem('userInfo') || '{}').username)
        const repoId = this.getAmcLevelNumber(this.selectedLevel)
        const knowledgeLevel = this.getKnowledgeBackgroundNumber(this.selectedKnowledgeBackground)

        // 检查是否已有该等级的计划
        let existingPlan = null
        try {
          const existRes = await getPlanByUserIdAndRepoId(userId, repoId, this.selectedSessionCode)
          if (existRes?.data) {
            existingPlan = existRes.data
          }
        } catch (e) {
          // 不存在则为 null
        }

        if (existingPlan) {
          // 更新现有计划
          const updateData = {
            id: existingPlan.id,
            isActive: 1,
            knowledgeLevel,
            targetYear: this.selectedYear,
            sessionCode: this.selectedSessionCode || null
          }
          console.log('更新学习计划:', updateData)
          await updatePlan(updateData)
        } else {
          // 创建新计划
          const planData = {
            userId,
            username,
            repoId,
            courseType: 'amc',
            isActive: 1,
            knowledgeLevel,
            targetYear: this.selectedYear,
            sessionCode: this.selectedSessionCode || null
          }
          console.log('创建学习计划:', planData)
          await createPlan(planData)
        }

        // 保存到 localStorage 作为备份
        const userSelection = {
          level: this.selectedLevel,
          knowledgeBackground: this.selectedKnowledgeBackground,
          year: this.selectedYear,
          sessionCode: this.selectedSessionCode,
          selectedAt: new Date().toISOString()
        }
        localStorage.setItem('amc_user_selection', JSON.stringify(userSelection))

        // 更新本地 userInfo，使路由守卫不再认为需要选课
        try {
          updateUserStatus('mark', 1)
          updateUserStatus('skipCourseSelection', true)
        } catch (e) {
          try {
            const ui = JSON.parse(localStorage.getItem('userInfo') || '{}')
            ui.mark = 1
            ui.skipCourseSelection = true
            localStorage.setItem('userInfo', JSON.stringify(ui))
          } catch (_) {}
        }

        this.$message.success('学习计划保存成功')
      } catch (error) {
        console.error('保存学习计划失败:', error)
        this.$message.error('保存失败: ' + (error.message || '请重试'))
        throw error
      }
    }
  }
}
</script>

<style scoped>
.amc-level-select-container {
  min-height: 100vh;
  background-color: #f5f7fa;
  padding: 0;
  margin: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* 顶部导航栏 */
.amc-header {
  background: #fff;
  padding: 15px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 15px;
}

.close-btn {
  font-size: 20px;
  color: #666;
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  transition: background-color 0.3s;
}

.close-btn:hover {
  background-color: #f5f5f5;
}

.page-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

/* 主要内容区域 */
.content-area {
  padding: 20px;
  max-width: 600px;
  margin: 0 auto;
}

/* 标题区域 */
.title-section {
  text-align: center;
  margin-bottom: 30px;
}

.main-title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0 0 10px 0;
}

.subtitle {
  font-size: 16px;
  color: #666;
  margin: 0;
}

/* AMC级别选择 */
.level-selection {
  margin-bottom: 30px;
}

.level-card {
  background: #fff;
  border: 2px solid #e1e5e9;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.level-card:hover {
  border-color: #409eff;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.15);
}

.level-card.selected {
  border-color: #409eff;
  background: #f0f9ff;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.2);
}

.level-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.level-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.grade-tag {
  background: #e3f2fd;
  color: #1976d2;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

.level-description {
  font-size: 14px;
  line-height: 1.6;
  color: #666;
  margin: 0;
}

/* 知识背景选择 */
.knowledge-background-selection {
  margin-bottom: 30px;
}

.knowledge-options {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.knowledge-option {
  background: #fff;
  border: 2px solid #e1e5e9;
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 15px;
}

.knowledge-option:hover {
  border-color: #409eff;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.15);
}

.knowledge-option.selected {
  border-color: #409eff;
  background: #f0f9ff;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.2);
}

.option-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #f5f7fa;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.knowledge-option.selected .option-icon {
  background: #409eff;
  color: #fff;
}

.option-icon i {
  font-size: 18px;
  color: #666;
}

.knowledge-option.selected .option-icon i {
  color: #fff;
}

.option-content {
  flex: 1;
}

.option-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0 0 5px 0;
}

.option-description {
  font-size: 14px;
  color: #666;
  margin: 0;
  line-height: 1.4;
}

/* 年份选择 */
.year-selection {
  margin-bottom: 30px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0 0 15px 0;
}

.year-selector {
  width: 100%;
}

.year-selector .el-input__inner {
  height: 50px;
  border-radius: 8px;
  border: 2px solid #e1e5e9;
  font-size: 16px;
}

.year-selector .el-input__inner:focus {
  border-color: #409eff;
}

/* 开始按钮 */
.action-section {
  text-align: center;
}

.start-button {
  width: 100%;
  height: 50px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  transition: all 0.3s ease;
}

.start-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.start-button:disabled {
  background: #c0c4cc;
  cursor: not-allowed;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .content-area {
    padding: 15px;
  }

  .main-title {
    font-size: 20px;
  }

  .subtitle {
    font-size: 14px;
  }

  .level-card {
    padding: 15px;
  }

  .level-title {
    font-size: 18px;
  }

  .level-description {
    font-size: 13px;
  }

  .section-title {
    font-size: 16px;
  }

  .knowledge-option {
    padding: 15px;
  }

  .option-icon {
    width: 35px;
    height: 35px;
  }

  .option-icon i {
    font-size: 16px;
  }

  .option-title {
    font-size: 15px;
  }

  .option-description {
    font-size: 13px;
  }

  .start-button {
    height: 45px;
    font-size: 15px;
  }
}

@media (max-width: 480px) {
  .level-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .grade-tag {
    align-self: flex-start;
  }
}
</style>
